import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import apiService from '@/services/api';
import AdminLayout from '@/components/layout/AdminLayout';
import { Search, Plus, MessageCircle } from 'lucide-react';

// (audit 2026-06-11) — was a stub page that only rendered a heading and a
// paragraph. Now lists provider-support tickets from /admin/providers/support
// and lets admin create one + search.
const AdminProviderSupportPage = () => {
  const [ticketSearch, setTicketSearch] = useState('');
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const response = await apiService.request('/admin/providers/support', 'GET');
      if (response?.success && response.data) {
        setTickets(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error fetching support tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTickets(); }, []);

  const handleCreateTicket = async () => {
    const title = window.prompt('Title for the new support ticket?');
    if (!title || !title.trim()) return;
    const description = window.prompt('Describe the issue (optional):', '') || '';
    setCreating(true);
    try {
      const response = await apiService.request('/admin/providers/support', 'POST', {
        title,
        description,
        priority: 'NORMAL',
      });
      if (response?.success) {
        alert('Support ticket created');
        await loadTickets();
      } else {
        alert('Error: ' + (response?.message || 'Failed to create ticket'));
      }
    } catch (error: any) {
      alert('Failed: ' + (error?.message || 'Network error'));
    } finally {
      setCreating(false);
    }
  };

  const filteredTickets = tickets.filter((t) => {
    const q = ticketSearch.trim().toLowerCase();
    if (!q) return true;
    return [t.title, t.description, t.category, t.provider_name, t.status]
      .map((v) => String(v || '').toLowerCase())
      .some((s) => s.includes(q));
  });

  const statusColor = (s?: string) => {
    const v = String(s || '').toLowerCase();
    if (v === 'open') return 'bg-blue-100 text-blue-700';
    if (v === 'resolved' || v === 'closed') return 'bg-green-100 text-green-700';
    if (v === 'pending' || v === 'in_progress') return 'bg-orange-100 text-orange-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <>
      <Helmet>
        <title>Provider Support - Virewire Admin</title>
      </Helmet>
      <AdminLayout>
        <div className="bg-gray-50 min-h-screen p-8">
          <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-4">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">Provider Support</h1>
                <p className="text-gray-600">Support tickets and provider communication management.</p>
              </div>
              <button
                onClick={handleCreateTicket}
                disabled={creating}
                className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white font-bold px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {creating ? 'Creating…' : 'New Ticket'}
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-6 mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-1">Open</p>
                <p className="text-2xl font-bold text-blue-700">
                  {tickets.filter((t) => String(t.status || '').toLowerCase() === 'open').length}
                </p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-1">In Progress / Pending</p>
                <p className="text-2xl font-bold text-orange-700">
                  {tickets.filter((t) => ['pending', 'in_progress'].includes(String(t.status || '').toLowerCase())).length}
                </p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-xs uppercase font-bold text-gray-600 mb-1">Resolved</p>
                <p className="text-2xl font-bold text-green-700">
                  {tickets.filter((t) => ['resolved', 'closed'].includes(String(t.status || '').toLowerCase())).length}
                </p>
              </div>
            </div>

            <div className="relative mb-4">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={ticketSearch}
                onChange={(e) => setTicketSearch(e.target.value)}
                placeholder="Search by title, provider, status…"
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Ticket</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Provider</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
                  )}
                  {!loading && filteredTickets.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No support tickets found.</td></tr>
                  )}
                  {!loading && filteredTickets.map((t) => (
                    <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <MessageCircle className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{t.title || `Ticket #${t.id}`}</p>
                            {t.description && <p className="text-xs text-gray-500 line-clamp-1">{t.description}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{t.provider_name || t.provider_id || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{t.priority || 'medium'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${statusColor(t.status)}`}>
                          {String(t.status || 'open').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {t.created_at ? new Date(t.created_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminProviderSupportPage;
